// The shell's theme preference (docs/09 §4: light / dark / system lives in
// the avatar menu, persisted as a display preference — never a credential).
// Extracted from AppLayout so the layout keeps only navigation chrome: the
// effective theme is the explicit preference, or the OS scheme while in
// system mode, tracked live via the media query.
import { useEffect, useState } from "react";

import type { ThemePreference } from "@marker/ui";

import {
  applyTheme,
  persistThemePreference,
  readThemePreference,
  resolveEffectiveTheme,
  systemPrefersDark,
} from "../lib/preferences";

export interface ShellTheme {
  preference: ThemePreference;
  select: (preference: ThemePreference) => void;
}

export function useShellTheme(): ShellTheme {
  const [preference, setPreference] =
    useState<ThemePreference>(readThemePreference);
  const [prefersDark, setPrefersDark] = useState(systemPrefersDark);

  useEffect(() => {
    if (
      preference !== "system" ||
      typeof window === "undefined" ||
      typeof window.matchMedia !== "function"
    ) {
      return;
    }
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    setPrefersDark(mediaQuery.matches);
    const onChange = (event: MediaQueryListEvent) => {
      setPrefersDark(event.matches);
    };
    mediaQuery.addEventListener("change", onChange);
    return () => {
      mediaQuery.removeEventListener("change", onChange);
    };
  }, [preference]);

  const effectiveTheme = resolveEffectiveTheme(preference, prefersDark);
  useEffect(() => {
    applyTheme(effectiveTheme);
  }, [effectiveTheme]);

  return {
    preference,
    select: (next: ThemePreference) => {
      persistThemePreference(next);
      setPreference(next);
    },
  };
}
